/**
 * This component will add Playback functionality for all media across the app using Video.js
 *
 * @module views/components/Player
 * @memberof -Common
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import { useNavigate } from 'react-router-dom'
import { ANALYTICS_EVENTS, DEVICE_ACCESS_CONTROL, PLAYER } from '../../../constant';
import './Player.scss';
import moment from 'moment';
import { checkConcurrency, getOttSettings } from '../../../Api/api';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { DialogButton } from '../../homePage/components/dialogButton';
import AnalyticsManager from '../../../analytics/analyticsManager';
import { utility } from '../../../utility/utility';
import { createThumbnailPreview, setupThumbnailHover } from './ThumbnailPreview';

/**
 * Represents a Player component
 *
 * @method
 * @param {object} props - React properties passed from composition
 * @returns Player
 */
export const Player = function (props) {

    let {src, asset, showControls = true } = props;
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const playerRef = useRef(null);

    // Activity management refs
    const userActivityTimeoutRef = useRef(null);
    const isUserActiveRef = useRef(true);
    const lastActivityTimeRef = useRef(Date.now());

    const [showMarker, _setShowMarker] = useState({
        show: false,
        data: null
    });

    const showMarkerRef = useRef(showMarker);
    const setShowMarker = (data) => {
        showMarkerRef.current = data;
        _setShowMarker(data);
    }

    const [isMarkerFocused, _setIsMarkerFocused] = useState(false);
    const isMarkerFocusedRef = useRef(isMarkerFocused);
    const setIsMarkerFocused = (data) => {
        isMarkerFocusedRef.current = data;
        _setIsMarkerFocused(data);
    }

    const [openDialog, setOpenDialog] = useState(false);

    const videoJsOptions = {
        autoplay: true,
        muted: false,
        controls: showControls ?? true,
        fluid: true,
        loadingSpinner: false,
        inactivityTimeout: 4000,
        html5: {
            vhs: {
                overrideNative: true
            },
            nativeAudioTracks: false,
            nativeVideoTracks: false
        }
    };

    const INACTIVITY_DELAY = 3000; // 3 seconds
    const FULLSCREEN_INACTIVITY_DELAY = 2000; // 2 seconds in fullscreen

    // Helper function to detect live streams
    const isLiveStream = (player) => {
        if (!player) return false;
        const duration = player.duration();

        if (duration === Infinity || isNaN(duration)) return true;

        const sources = player.currentSources();
        if (sources && sources.length > 0) {
            const src = sources[0].src;
            if (src && src.includes('.m3u8')) {
                if (player.liveTracker && player.liveTracker.isLive()) {
                    return true;
                }
            }
        }

        return false;
    };

    // User Activity Management Functions
    const showAllControls = useCallback(() => {
        isUserActiveRef.current = true;
        const playerContainer = document.getElementById('player');
        if (playerContainer) {
            playerContainer.classList.remove('player-inactive');
            playerContainer.classList.add('player-active');
            playerContainer.style.cursor = 'default';
        }

        // Update Video.js internal state
        if (playerRef.current) {
            playerRef.current.userActive(true);
        }

        // Clear any existing timeout
        if (userActivityTimeoutRef.current) {
            clearTimeout(userActivityTimeoutRef.current);
        }

        // Set new timeout to hide controls
        const delay = document.fullscreenElement ? FULLSCREEN_INACTIVITY_DELAY : INACTIVITY_DELAY;
        userActivityTimeoutRef.current = setTimeout(() => {
            hideAllControls();
        }, delay);
    }, []);

    const hideAllControls = useCallback(() => {
        // Only hide if video is playing
        if (playerRef.current && !playerRef.current.paused()) {
            isUserActiveRef.current = false;
            const playerContainer = document.getElementById('player');
            if (playerContainer) {
                playerContainer.classList.remove('player-active');
                playerContainer.classList.add('player-inactive');
                playerContainer.style.cursor = 'none';
            }

            // Update Video.js internal state
            if (playerRef.current) {
                playerRef.current.userActive(false);
            }
        }
    }, []);

    const resetActivityTimer = useCallback(() => {
        lastActivityTimeRef.current = Date.now();

        if (!isUserActiveRef.current) {
            showAllControls();
        } else {
            // Reset the timer
            if (userActivityTimeoutRef.current) {
                clearTimeout(userActivityTimeoutRef.current);
            }
            const delay = document.fullscreenElement ? FULLSCREEN_INACTIVITY_DELAY : INACTIVITY_DELAY;
            userActivityTimeoutRef.current = setTimeout(() => {
                hideAllControls();
            }, delay);
        }
    }, [showAllControls, hideAllControls]);

    const onKeyPress = useCallback((e) => {
        const { keyCode } = e;

        // Reset activity timer on any key press
        resetActivityTimer();

        if (keyCode === 10009 || keyCode == PLAYER.VK_BACK || keyCode == PLAYER.EM_BACK || keyCode == PLAYER.VK_BACK_SPACE) {
            e.preventDefault();
            onBack();
        }

        if (keyCode === PLAYER.PLAY || keyCode === PLAYER.VK_PLAY || keyCode === PLAYER.EM_PLAY) {
            if (playerRef.current) {
                playerRef.current.play();
            }
        }

        if (keyCode === PLAYER.PAUSE || keyCode === PLAYER.VK_PAUSE || keyCode === PLAYER.EM_PAUSE) {
            if (playerRef.current) {
                playerRef.current.pause();
                AnalyticsManager().logEvent(ANALYTICS_EVENTS.CONTENT_PAUSE, asset);
            }
        }

        if (keyCode === PLAYER.ENTER || keyCode === PLAYER.VK_ENTER || keyCode === PLAYER.VK_PLAY_PAUSE) {
            if (showMarkerRef.current.show && isMarkerFocusedRef.current) {
                if (playerRef.current) {
                    const inTimeSeconds = moment.duration(showMarkerRef.current.data.Out, 'milliseconds').asSeconds();
                    playerRef.current.currentTime(inTimeSeconds);
                    setIsMarkerFocused(false);
                    setShowMarker({ show: false, data: null });
                }
            } else if (playerRef.current) {
                if (playerRef.current.paused()) {
                    playerRef.current.play();
                } else {
                    playerRef.current.pause();
                }
            }
        }

        if (keyCode === PLAYER.STOP || keyCode === PLAYER.VK_STOP) {
            if (playerRef.current) {
                playerRef.current.currentTime(0);
                playerRef.current.pause();
                AnalyticsManager().logEvent(ANALYTICS_EVENTS.CONTENT_PAUSE, asset);
            }
        }

        if (keyCode == PLAYER.LEFT || keyCode == PLAYER.VK_LEFT || keyCode == PLAYER.EM_REWIND || keyCode == PLAYER.VK_REWIND || keyCode == PLAYER.EM_PREV || keyCode == PLAYER.VK_PREV) {
            if (playerRef.current) {
                playerRef.current.currentTime(playerRef.current.currentTime() - 10);
            }
        }

        if (keyCode == PLAYER.RIGHT || keyCode == PLAYER.VK_RIGHT || keyCode == PLAYER.EM_FAST_FWD || keyCode == PLAYER.VK_FAST_FWD || keyCode == PLAYER.EM_NEXT || keyCode == PLAYER.VK_NEXT) {
            if (playerRef.current) {
                playerRef.current.currentTime(playerRef.current.currentTime() + 10);
            }
        }

        if (keyCode == PLAYER.UP || keyCode == PLAYER.VK_UP) {
            setIsMarkerFocused(true);
        }

        if (keyCode == PLAYER.DOWN || keyCode == PLAYER.VK_DOWN) {
            setIsMarkerFocused(false);
        }
    }, [resetActivityTimer]);

    const { ref: dialogRef, focusKey: dialogFocusKey, focusSelf: focusDialog } = useFocusable({
        focusable: true,
        saveLastFocusedChild: true,
        trackChildren: true,
        autoRestoreFocus: true,
        isFocusBoundary: true,
        focusKey: "dialog-container",
        preferredChildFocusKey: "dialog-cancel-button",
    });

    const toggleMaximize = () => {
        const playerElement = document.getElementById('player');

        if (document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        } else {
            if (playerElement.requestFullscreen) {
                playerElement.requestFullscreen();
            } else if (playerElement.webkitRequestFullscreen) {
                playerElement.webkitRequestFullscreen();
            } else if (playerElement.msRequestFullscreen) {
                playerElement.msRequestFullscreen();
            }
        }
    };

    useEffect(() => {
        if (showControls) {
            document.addEventListener('keydown', onKeyPress);
            return () => document.removeEventListener('keydown', onKeyPress);
        }
    }, [onKeyPress, showControls]);

    useEffect(() => {
        onCheckConcurrency();
    }, []);

    useEffect(() => {
        const player = playerRef.current;
        if (player) {
            player.on('ended', () => {
                onBack();
            });
        }
        return () => {
            if (player) {
                player.dispose();
                playerRef.current = null;
            }
            // Clean up activity timeout
            if (userActivityTimeoutRef.current) {
                clearTimeout(userActivityTimeoutRef.current);
            }
        };
    }, [playerRef]);

    const onCheckConcurrency = async () => {
        let user = JSON.parse(localStorage.getItem('user'));
        const ottSettingRes = await getOttSettings();
        if (!ottSettingRes?.success) {
            navigate(-1);
            return;
        }
        if (ottSettingRes?.data?.deviceAccessControl == DEVICE_ACCESS_CONTROL.Login || ottSettingRes?.data?.concurrency == 0) {
            return;
        }
        setInterval(async () => {
            try {
                if (playerRef.current) {
                    let res = await checkConcurrency(user._id, (user.Name + '`s Device'));
                    if (!res.success) {
                        playerRef.current.pause();
                        setOpenDialog(res.message);
                        focusDialog();
                    }
                }
            } catch (error) {
                AnalyticsManager().logEvent(ANALYTICS_EVENTS.PLAYBACK_ERROR, error);
                console.log('something went wrong while checking concurrency', error);
            }
        }, ottSettingRes?.data?.concurrency ?? 30000);
    }

    const closeDialog = () => {
        setOpenDialog(false);
        navigate(-1);
    };

    const onBack = () => {
        const player = playerRef.current;
        if (player) {
            playerRef.current = null;
        }
        navigate(-1)
    }

    const onReady = (player) => {
        console.log('onReady', player);

        let playerContainer = document.getElementById('player');
        let titleElement = document.createElement('div');
        titleElement.setAttribute('id', 'playerTitle');
        titleElement.innerHTML = asset.Title;
        titleElement.className = 'player-title';

        // Back button
        let backElement = document.createElement('div');
        backElement.setAttribute('id', 'backIcon');
        backElement.setAttribute("aria-hidden", true);
        backElement.style.cursor = 'pointer';
        backElement.innerHTML = '<i class="fa fa-arrow-left" aria-hidden="true"></i>';
        backElement.className = 'vjs-back-icon';

        // Next Episode button - only show if utility function returns true
        let nextEpisodeButton = null;
        if (utility.isShowingNextEpisode()) {
            nextEpisodeButton = document.createElement('div');
            nextEpisodeButton.setAttribute('id', 'nextEpisodeButton');
            nextEpisodeButton.className = 'next-episode-button';
            nextEpisodeButton.innerHTML = `
                <i class="fa fa-step-forward" aria-hidden="true"></i>
                <span>Next Episode</span>
            `;
        }

        // Skip Intro button
        let skipIntroButton = document.createElement('div');
        skipIntroButton.setAttribute('id', 'skipIntroButton');
        skipIntroButton.className = 'skip-intro-button';
        skipIntroButton.style.display = 'none';
        skipIntroButton.innerHTML = `
            <div class="skip-intro-content">
                <span class="skip-intro-text">Skip Intro</span>
                <i class="fa fa-chevron-right skip-intro-icon" aria-hidden="true"></i>
            </div>
        `;

        // Subtitle display container
        let subtitleContainer = document.createElement('div');
        subtitleContainer.setAttribute('id', 'subtitleContainer');
        subtitleContainer.className = 'subtitle-display-container';

        let subtitleText = document.createElement('div');
        subtitleText.setAttribute('id', 'subtitleText');
        subtitleText.className = 'subtitle-text';
        subtitleContainer.appendChild(subtitleText);

        // Create complete control bar container
        let controlBarContainer = document.createElement('div');
        controlBarContainer.className = 'custom-control-bar';

        // Check if stream is live to conditionally show LIVE indicator
        const showLiveIndicator = isLiveStream(player);
        console.log(showLiveIndicator);

        controlBarContainer.innerHTML = `
            <div class="control-left">
                <button class="control-btn play-pause-btn" id="customPlayPause">
                    <i class="fa fa-play" aria-hidden="true"></i>
                </button>
                <div class="time-display">
                    <span class="current-time">00:00</span>
                    <span class="time-separator">/</span>
                    <span class="duration-time">00:00</span>
                </div>
            </div>
            
            <div class="control-center">
                <div class="progress-container">
                    <div class="progress-track">
                        <div class="progress-loaded"></div>
                        <div class="progress-played"></div>
                        <div class="progress-handle"></div>
                    </div>
                </div>
            </div>
            
            <div class="control-right">
                <button class="control-btn volume-btn" id="customVolume">
                    <i class="fa fa-volume-up" aria-hidden="true"></i>
                </button>
                <div class="volume-slider-container">
                    <div class="volume-track">
                        <div class="volume-fill"></div>
                        <div class="volume-handle"></div>
                    </div>
                </div>
                <button class="control-btn subtitle-btn" id="subtitleButton">
                    <i class="fa fa-cc" aria-hidden="true"></i>
                </button>
                <button class="control-btn fullscreen-btn" id="customFullscreen">
                    <i class="fa fa-expand" aria-hidden="true"></i>
                </button>
                ${showLiveIndicator ? '<div class="live-indicator">LIVE</div>' : ''}
            </div>
        `;

        playerContainer?.appendChild(titleElement);
        playerContainer?.appendChild(backElement);
        if (nextEpisodeButton) {
            playerContainer?.appendChild(nextEpisodeButton);
        }
        playerContainer?.appendChild(skipIntroButton);
        playerContainer?.appendChild(subtitleContainer);
        playerContainer?.appendChild(controlBarContainer);

        // Hide default Video.js controls
        const defaultControlBar = playerContainer?.querySelector('.vjs-control-bar');
        if (defaultControlBar) {
            defaultControlBar.style.display = 'none';
        }

        // Activity detection events
        const activityEvents = [
            'mousemove', 'mousedown', 'mouseup', 'click',
            'keydown', 'keyup', 'touchstart', 'touchmove', 'touchend'
        ];

        activityEvents.forEach(eventType => {
            playerContainer.addEventListener(eventType, resetActivityTimer, true);
        });

        // Throttled mousemove handler for better performance
        let mouseMoveTimeout;
        const handleMouseMove = (e) => {
            // Only trigger if mouse actually moved (not just entered/left)
            if (e.movementX !== 0 || e.movementY !== 0) {
                if (mouseMoveTimeout) return;
                mouseMoveTimeout = setTimeout(() => {
                    resetActivityTimer();
                    mouseMoveTimeout = null;
                }, 100);
            }
        };

        playerContainer.addEventListener('mousemove', handleMouseMove);

        // Player event handlers
        player.on('play', () => {
            showAllControls();
            const playPauseIcon = document.querySelector('#customPlayPause i');
            if (playPauseIcon) playPauseIcon.className = 'fa fa-pause';
        });

        player.on('pause', () => {
            showAllControls();
            // Keep controls visible when paused
            if (userActivityTimeoutRef.current) {
                clearTimeout(userActivityTimeoutRef.current);
                userActivityTimeoutRef.current = null;
            }
            const playPauseIcon = document.querySelector('#customPlayPause i');
            if (playPauseIcon) playPauseIcon.className = 'fa fa-play';
        });

        player.on('ended', () => {
            showAllControls();
            if (userActivityTimeoutRef.current) {
                clearTimeout(userActivityTimeoutRef.current);
                userActivityTimeoutRef.current = null;
            }
        });

        player.on('seeking', () => {
            showAllControls();
        });

        player.on('volumechange', () => {
            showAllControls();
        });

        // Handle fullscreen changes
        const handleFullscreenChange = () => {
            resetActivityTimer(); // Reset timer with appropriate delay for fullscreen
            const fullscreenIcon = document.querySelector('#customFullscreen i');
            if (fullscreenIcon) {
                if (document.fullscreenElement || document.webkitIsFullScreen) {
                    fullscreenIcon.className = 'fa fa-compress';
                } else {
                    fullscreenIcon.className = 'fa fa-expand';
                }
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

        // Event listeners
        backElement.addEventListener("click", onBack);

        if (nextEpisodeButton) {
            nextEpisodeButton.addEventListener("click", () => {
                playNextEpisode();
            });
        }

        // Custom play/pause functionality
        const playPauseBtn = document.getElementById('customPlayPause');
        playPauseBtn?.addEventListener('click', () => {
            if (player.paused()) {
                player.play();
            } else {
                player.pause();
            }
        });

        // Volume control functionality
        const volumeBtn = document.getElementById('customVolume');
        const volumeIcon = volumeBtn?.querySelector('i');
        const volumeSlider = document.querySelector('.volume-slider-container');
        const volumeTrack = document.querySelector('.volume-track');
        const volumeFill = document.querySelector('.volume-fill');
        const volumeHandle = document.querySelector('.volume-handle');

        const updateVolumeDisplay = () => {
            const volume = player.volume();
            const muted = player.muted();
            const volumePercent = muted ? 0 : volume * 100;

            if (muted || volume === 0) {
                volumeIcon.className = 'fa fa-volume-off';
            } else if (volume < 0.5) {
                volumeIcon.className = 'fa fa-volume-down';
            } else {
                volumeIcon.className = 'fa fa-volume-up';
            }

            volumeFill.style.width = `${volumePercent}%`;
            volumeHandle.style.left = `${volumePercent}%`;
        };

        volumeBtn?.addEventListener('click', () => {
            player.muted(!player.muted());
            updateVolumeDisplay();
        });

        // Volume slider functionality
        const handleVolumeChange = (e) => {
            const rect = volumeTrack.getBoundingClientRect();
            let percent = (e.clientX - rect.left) / rect.width;
            percent = Math.max(0, Math.min(1, percent));
            player.volume(percent);
            if (percent > 0) player.muted(false);
            updateVolumeDisplay();
        };

        let isDraggingVolume = false;
        volumeSlider?.addEventListener('mousedown', (e) => {
            isDraggingVolume = true;
            handleVolumeChange(e);
        });

        document.addEventListener('mousemove', (e) => {
            if (isDraggingVolume) handleVolumeChange(e);
        });

        document.addEventListener('mouseup', () => {
            isDraggingVolume = false;
        });

        // Progress bar functionality
        const progressContainer = document.querySelector('.progress-container');
        const progressTrack = document.querySelector('.progress-track');
        const progressPlayed = document.querySelector('.progress-played');
        const progressHandle = document.querySelector('.progress-handle');
        const currentTimeDisplay = document.querySelector('.current-time');
        const durationDisplay = document.querySelector('.duration-time');

        // Create thumbnail preview using component
        const { thumbnailPreview, thumbnailImg, thumbnailTime } = createThumbnailPreview(progressContainer);
        let thumbnails = [];
        // sample vtt: "https://res.cloudinary.com/dkzax2qbn/raw/upload/v1758623331/thumbnails_bsdo6c.vtt"
        // Load thumbnails only if asset has thumbnailUrl
        if (asset?.thumbnailUrl && asset?.thumbnailUrl.length > 0) {
            const loadThumbnails = async () => {
                thumbnails = await utility.loadVTTThumbnails(asset.thumbnailUrl);
                // Setup hover events after thumbnails are loaded
                setupThumbnailHover(progressContainer, progressTrack, thumbnails, thumbnailPreview, thumbnailImg, thumbnailTime, player, isLiveStream);
            };
            loadThumbnails();
        }

        const formatTime = (seconds) => {
            if (!isFinite(seconds)) return 'LIVE';
            const minutes = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        };

        const updateProgress = () => {
            const currentTime = player.currentTime();
            const duration = player.duration();

            // Don't show progress bar for live streams
            if (isLiveStream(player)) {
                progressContainer.style.visibility = 'hidden';
                currentTimeDisplay.textContent = 'LIVE';
                durationDisplay.textContent = '';
            } else {
                progressContainer.style.visibility = 'visible';
                if (duration > 0) {
                    const percent = (currentTime / duration) * 100;
                    progressPlayed.style.width = `${percent}%`;
                    progressHandle.style.left = `${percent}%`;
                }
                currentTimeDisplay.textContent = formatTime(currentTime);
                durationDisplay.textContent = formatTime(duration || 0);
            }
        };

        const handleProgressChange = (e) => {
            if (isLiveStream(player)) return;

            const rect = progressTrack.getBoundingClientRect();
            let percent = (e.clientX - rect.left) / rect.width;
            percent = Math.max(0, Math.min(1, percent));
            const newTime = percent * player.duration();
            player.currentTime(newTime);
        };

        let isDraggingProgress = false;
        progressContainer?.addEventListener('mousedown', (e) => {
            if (!isLiveStream(player)) {
                isDraggingProgress = true;
                handleProgressChange(e);
            }
        });

        document.addEventListener('mousemove', (e) => {
            if (isDraggingProgress) handleProgressChange(e);
        });

        document.addEventListener('mouseup', () => {
            isDraggingProgress = false;
        });

        // Fullscreen functionality
        const fullscreenBtn = document.getElementById('customFullscreen');
        fullscreenBtn?.addEventListener('click', toggleMaximize);

        // Subtitle functionality
        let subtitlesEnabled = false;
        let currentSubtitleTrack = null;

        const initializeSubtitles = () => {
            // Hook into first track for display
            let hasSubtitle = false;
            const textTracks = player.textTracks();
            for (let i = 0; i < textTracks.length; i++) {
                const track = textTracks[i];
                if (track.kind === 'subtitles' || track.kind === 'captions') {
                    currentSubtitleTrack = track;
                    track.addEventListener('cuechange', updateSubtitleDisplay);
                    track.mode = 'hidden';
                    hasSubtitle = true;
                    break;
                }
            }
            // If no subtitles found → disabled the subtitle button
            const subtitleBtn = document.getElementById('subtitleButton');
            if (subtitleBtn) {
                subtitleBtn.disabled = !hasSubtitle;
                subtitleBtn.classList.toggle('disabled', !hasSubtitle); // optional CSS styling
            }

        };

        const updateSubtitleDisplay = () => {
            const subtitleTextElement = document.getElementById('subtitleText');
            if (!subtitleTextElement || !currentSubtitleTrack) return;

            if (subtitlesEnabled && currentSubtitleTrack.activeCues && currentSubtitleTrack.activeCues.length > 0) {
                const cue = currentSubtitleTrack.activeCues[0]
                subtitleTextElement.innerHTML = cue.text ? cue.text?.includes(":") ?  cue.text.split(":")[1] : cue.text : "";
                subtitleTextElement.style.display = 'block';
            } else {
                subtitleTextElement.style.display = 'none';
            }
        };

        const toggleSubtitles = () => {
            subtitlesEnabled = !subtitlesEnabled;
            const subtitleBtn = document.getElementById('subtitleButton');
            const subtitleIcon = subtitleBtn?.querySelector('i');

            if (subtitlesEnabled) {
                if (currentSubtitleTrack) {
                    currentSubtitleTrack.mode = 'showing';
                }
                subtitleIcon?.classList.add('active');
            } else {
                if (currentSubtitleTrack) {
                    currentSubtitleTrack.mode = 'hidden';
                }
                subtitleIcon?.classList.remove('active');
                document.getElementById('subtitleText').style.display = 'none';
            }

            updateSubtitleDisplay();
        };

        const subtitleBtn = document.getElementById('subtitleButton');
        subtitleBtn?.addEventListener('click', toggleSubtitles);

        // Skip Intro functionality
        let skipIntroTimeout;

        const showSkipIntro = (targetTime) => {
            const skipButton = document.getElementById('skipIntroButton');
            if (skipButton) {
                skipButton.style.display = 'flex';
                skipButton.style.opacity = '1';

                skipIntroTimeout = setTimeout(() => {
                    hideSkipIntro();
                }, 10000);

                const clickHandler = () => {
                    player.currentTime(targetTime);
                    hideSkipIntro();
                    resetActivityTimer();
                    AnalyticsManager().logEvent(ANALYTICS_EVENTS.SKIP_INTRO, asset);
                };

                skipButton.removeEventListener('click', skipButton.currentClickHandler);
                skipButton.addEventListener('click', clickHandler);
                skipButton.currentClickHandler = clickHandler;
            }
        };

        const hideSkipIntro = () => {
            const skipButton = document.getElementById('skipIntroButton');
            if (skipButton) {
                skipButton.style.opacity = '0';
                setTimeout(() => {
                    skipButton.style.display = 'none';
                }, 300);

                if (skipIntroTimeout) {
                    clearTimeout(skipIntroTimeout);
                }
            }
        };

        // Update displays
        player.on('timeupdate', () => {
            const currentTime = player.currentTime();

            updateProgress();

            // Check for intro markers
            if (asset?.IntroMarkers?.length > 0) {
                asset?.IntroMarkers?.forEach(intro => {
                    let startTime = moment.duration(intro.StartTime, 'milliseconds').asSeconds();
                    let endTime = moment.duration(intro.EndTime, 'milliseconds').asSeconds();

                    if (currentTime >= startTime && currentTime <= endTime) {
                        showSkipIntro(endTime);
                    } else if (currentTime > endTime) {
                        hideSkipIntro();
                    }
                });
            }

            // Check for subtitle markers
            if (asset?.SubtitleMarkers?.length > 0 && subtitlesEnabled) {
                asset?.SubtitleMarkers?.forEach(subtitle => {
                    let startTime = moment.duration(subtitle.StartTime, 'milliseconds').asSeconds();
                    let endTime = moment.duration(subtitle.EndTime, 'milliseconds').asSeconds();

                    if (currentTime >= startTime && currentTime <= endTime) {
                        const subtitleTextElement = document.getElementById('subtitleText');
                        if (subtitleTextElement) {
                            subtitleTextElement.innerHTML = subtitle.Text;
                            subtitleTextElement.style.display = 'block';
                        }
                    }
                });
            }

            // Existing skip marker logic
            if (asset?.Marker?.length > 0) {
                asset?.Marker?.map(m => {
                    let inTime = moment.duration(m.In, 'milliseconds').asSeconds();
                    let outTime = moment.duration(m.Out, 'milliseconds').asSeconds();
                    if (inTime <= currentTime && outTime >= currentTime && !showMarker.show) {
                        setShowMarker({
                            show: true,
                            data: m
                        });
                        setTimeout(() => {
                            setShowMarker({
                                show: false,
                                data: null
                            });
                        }, (outTime - currentTime) * 1000);
                    }
                });
            }
        });

        player.on('volumechange', updateVolumeDisplay);
        player.on('loadedmetadata', () => {
            updateProgress();
            initializeSubtitles();
        });

        // Initialize displays
        updateVolumeDisplay();
        updateProgress();

        // Initialize with controls visible
        showAllControls();

        // Hide skip intro when seeking or pausing
        player.on('seeking', () => {
            hideSkipIntro();
        });

        player.on('pause', () => {
            hideSkipIntro();
        });

        // Override Video.js built-in user activity
        player.off('userinactive');
        player.off('useractive');
    };

    const launchPlayer = () => {
        if (!playerRef.current) {
            const videoElement = videoRef.current;
            if (videoElement) {
                const player = (playerRef.current = videojs(videoElement, videoJsOptions, () => {
                    onReady && onReady(player);
                    // Add subtitles
                    if (asset?.Subtitles?.length > 0) {
                        asset.Subtitles.forEach(subtitle => {
                            return player.addRemoteTextTrack(subtitle, false);
                        })
                    }
                }));
            }
        }
    };

    useEffect(() => {
        videoJsOptions.sources = [{ src: src, type: "application/x-mpegURL", }];
        launchPlayer();
    }, []);

    const playNextEpisode = () => {
        window.location.reload();
        utility.onPlayNextEpisode(navigate);
    }

    return (
        <div className='Player player-active' data-vjs-player style={{ width: '100%', height: '100%' }}>
            {/* Video */}
            <video
                id='player'
                ref={videoRef}
                className='video-js'
                style={{
                    position: "fixed",
                    width: "100vw",
                    height: "100vh",
                    aspectRatio: "16/9",
                }}
            />

            {/* Dialog */}
            <Dialog open={openDialog} onClose={closeDialog} sx={{ "& .MuiDialog-paper": { outline: "none" } }}>
                <FocusContext.Provider value={dialogFocusKey}>
                    <Box ref={dialogRef}>
                        <DialogTitle>Alert</DialogTitle>
                        <DialogContent>{openDialog}</DialogContent>
                        <DialogActions>
                            <DialogButton onClick={closeDialog} color="primary" focusKey="dialog-cancel-button">
                                OK
                            </DialogButton>
                        </DialogActions>
                    </Box>
                </FocusContext.Provider>
            </Dialog>
        </div>
    );
}

/**
 * Property type definitions
 */
export const propTypes = {
    src: PropTypes.string,
    asset: PropTypes.object
};

Player.propTypes = propTypes;
export default Player;

